import { Errors } from '@equifinance/shared';
import { Stages } from '../../types/Stages';
import { LoanDetails } from './LoanDetails';
import { ApplicationStatuses } from '../applications';
import { Frequency, IncomeAndEmployment, OtherIncomeType } from './IncomeAndEmployment';

export enum Titles {
  Mr = 'Mr',
  Mrs = 'Mrs',
  Miss = 'Miss',
  Ms = 'Ms',
  Sir = 'Sir',
  Dame = 'Dame',
  Madam = 'Madam',
  Lady = 'Lady',
  Dr = 'Dr',
  Revd = 'Revd',
  Prof = 'Prof',
  Other = 'Other',
}

export enum MaritalStatus {
  Single = 'Single',
  Married = 'Married',
  CivilPartnership = 'Civil Partnership',
  Divorced = 'Divorced',
  Widowed = 'Widowed',
}

export enum CitizenshipStatus {
  BritishCitizen = 'British citizen',
  IrishCitizen = 'Citizen of Ireland',
  EUSettlementSchemeStatus = 'Settled or pre-settled status through EU settlement scheme',
  IndefiniteLeaveToEnter = 'Indefinite leave to enter (ILE)',
  IndefiniteLeaveToRemain = 'Indefinite leave to remain (ILR)',
  ExemptFromImmigrationControl = 'Exempt from immigration control',
  NoneOfTheAbove = 'None of the above',
}

export enum PlanToPayLoanRetire {
  WorkingBeyondNormalRetirementAge = 'Working beyond normal retirement age',
  SellingPropertyAndDownsizing = 'Selling Property and downsizing',
  SellingBtlProperty = 'Selling BTL property',
  SavingsOrInvestments = 'Savings or Investment',
  Pension = 'Pension',
  Other = 'Other',
}

export type Address = {
  id: string,
  subBuilding: string,
  houseName: string,
  houseNumber: string,
  street: string,
  postTown: string,
  postCode: string,
  country: string,
  region: string,
}

export type DatedAddress = {
  id: string,
  movedInDate: string,
  movedOutDate: string,
  isCurrentAddress: boolean,
  address: Address,
}

export enum SupportTypes {
  LHS = 'No or low access to help or support',
  LKC = 'Low knowledge or confidence in managing finances',
  LND = 'Learning difficulties',
  PDS = 'Poor or non-existent digital skills',
  PES = 'Poor English language skills',
  PLM = 'Poor literacy or numeracy skills',
  ADD = 'Addiction',
  AGE = 'Age',
  HVI = 'Hearing or visual impairment',
  LMC = 'Low mental capacity or cognitive disability',
  MHD = 'Mental health condition or disability',
  PDA = 'Physical disability',
  SLI = 'Severe or long-term illness',
  BVT = 'Bereavement',
  CRB = 'Caring responsibilities',
  DMA = 'Domestic abuse',
  ICS = 'Income shock',
  RBD = 'Relationship breakdown',
  RTM = 'Retirement',
  IEI = 'Inadequate or erratic income',
  LER = 'Low emotional resilience',
  LSV = 'Low savings',
  OID = 'Over-indebtedness',
  OTH = 'Other',
}

export enum ContactDetailsType {
  HomeNumber = 'Home number',
  PersonalMobileNumber = 'Personal mobile number',
  WorkMobileNumber = 'Work mobile number',
  PersonalEmailAddress = 'Personal email address',
  WorkEmailAddress = 'Work email address',
}

export type ContactDetails = {
  id: string,
  type: keyof typeof ContactDetailsType,
  info: string,
}

export enum EmploymentType {
  Employed = 'Employed',
  ConstructionIndustryScheme = 'Construction Industry Scheme',
  DirectorOfLimitedCompany = 'Director of a Limited Company ',
  SoleTrader = 'Sole Trader',
  LimitedLiabilityPartnership = 'Limited Liability Partnership',
  Partnership = 'Partnership',
  FosterCarer = 'Foster Carer',
  NotWorkingRetired = 'Not working (Retired)',
  NotWorkingNotRetired = 'Not working (Not Retired)',
}

export enum ContractType {
  PartTime = 'Part-time',
  FullTime = 'Full-time',
  FixedTermPartTime = 'Fixed term part-time',
  FixedTermFullTime = 'Fixed term full-time',
}

export enum ContractHistoryType {
  FixedTerm = 'Fixed term',
  DayRate = 'Day rate',
}

export enum IndustryType {
  AgricultureForestryAndFishing = 'Agriculture forestry and fishing',
  MiningAndQuarrying = 'Mining and quarrying',
  ManufacturingUtilities = 'Manufacturing utilities',
  Construction = 'Construction',
  AccommodationAndFoodServiceActivities = 'Accommodation and food service activities',
  TransportAndStorage = 'Transport and storage',
  InformationAndCommunication = 'Information and communication',
  FinancialAndInsuranceActivities = 'Financial and insurance activities',
  Education = 'Education',
  HumanHealthAndSocialWorkActivities = 'Human health and social work activities',
}

export enum NotWorkingStatus {
  ParentalLeaveSMP = 'Parental leave SMP',
  Unemployed = 'Unemployed',
  Student = 'Student',
  HousePerson = 'House person',
}

export enum JobTitle {
  ManagersDirectorsAndSeniorOfficials = 'Managers directors and senior officials',
  ProfessionalOccupations = 'Professional occupations',
  AssociateProfessionalOccupations = 'Associate professional occupations',
  AdministrativeAndSecretarialOccupations = 'Administrative and secretarial occupations',
  SkilledTradesOccupations = 'Skilled trades occupations',
  CaringLeisureAndOtherServiceOccupations = 'Caring leisure and other service occupations',
  SalesAndCustomerServiceOccupations = 'Sales and customer service occupations',
  ProcessPlantAndMachineOperatives = 'Process plant and machine operatives',
  ElementaryOccupations = 'Elementary occupations',
}

export enum Industry {
  AgricultureForestryAndFishing = 'Agriculture forestry and fishing',
  MiningAndQuarrying = 'Mining and quarrying',
  ManufacturingUtilities = 'Manufacturing utilities',
  Construction = 'Construction',
  AccommodationAndFoodServiceActivities = 'Accommodation and food service activities',
  TransportAndStorage = 'Transport and storage',
  InformationAndCommunication = 'Information and communication',
  FinancialAndInsuranceActivities = 'Financial and insurance activities',
  Education = 'Education',
  HumanHealthAndSocialWorkActivities = 'Human health and social work activities',
}

export type Position = {
  id: string,
  employmentType: keyof typeof EmploymentType,
  contractType: keyof typeof ContractType,
  jobTitle: JobTitle,
  industry: Industry,
  weeklyHours: number,
  isCurrent: boolean,
  startDate: string,
  endDate: string,
  companyName: string,
  companyRegistrationNumber: string,
  isCompanyAddressSameAsSecurityAddress: boolean,
  companyAddress: Address,
  registeredLimitedCompany: boolean,
  notWorkingStatus: 'ParentalLeaveSMP' | 'Unemployed' | 'Student' | 'HousePerson',
};

export type ContractHistory = {
  id: string,
  contractType: keyof typeof ContractHistoryType,
  companyName: string,
  industry: Industry,
  currentContract: boolean,
  rollingContract: boolean,
  startDate: string,
  endDate: string,
  totalGrossAnnualIncome: number,
  netMonthlyIncome: number,
  grossDayRate: number,
  contractedDaysPerWeek: number,
}

export enum DocumentUsedEvidenceValueType {
  SA302 = 'SA302',
  AccountantCertificate = 'AccountantCertificate',
  AnnualAccounts = 'AnnualAccounts',
}

export type DocumentUsedEvidenceAccountantCertificate = {
  taxYear: string,
  submissionDate?: string,
  applicantSalaryDirector?: number,
  applicantDividendDistribution?: number,
  netMonthlyIncome?: number,
  id: string,
}

export type DocumentUsedEvidenceAnnualAccount = {
  taxYear: string,
  submissionDate?: string,
  netProfitAfterCompanyTax?: number,
  netMonthlyIncome?: number,
  id: string,
}

export type WorkHistory = {
  id: string,
  position: Position,
  employerDetails: {
    id: string,
    employerName: string,
    employerAddress: Address
  },
  income: {
    id: string,
    totalGrossAnnualIncome: number,
    netMonthlyIncome: number,
    documentUsedEvidenceSelfEmployment: keyof typeof DocumentUsedEvidenceValueType,
    documentUsedEvidenceSA302: {
      id: string,
      totalIncome: number,
      taxAndNIDeductions: number,
      totalNetMonthlyIncome: number,
    },
    documentUsedEvidenceAccountantCertificates: {
      id: string,
      data: DocumentUsedEvidenceAccountantCertificate[],
    },
    documentUsedEvidenceAnnualAccounts: {
      id: string,
      data: DocumentUsedEvidenceAnnualAccount[],
    },
  },
  contractHistories: ContractHistory[],
  accountantDetails: {
    id: string,
    applicantHasAccountant: boolean,
    accountancyFirmName: string,
    accountancyQualification: string,
    provideQualificationDetails: string,
    accountantMembershipNo: string,
    contactName: string,
    contactNumber: string,
  },
};

export type IncomeTypeKeys = keyof typeof OtherIncomeType;

export type PensionOrNonJobRelatedIncome = {
  id: string,
  incomeType: keyof typeof OtherIncomeType,
  amount: number,
  frequency: keyof typeof Frequency,
}

export type Applicant = {
  id: string,
  incomeAndEmployment: IncomeAndEmployment,
  personalDetails: {
    id: string,
    title: keyof typeof Titles,
    firstName: string,
    middleName: string,
    lastName: string,
    previousLastName: string,
    dateOfBirth: string,
    plannedRetirementAge: number,
    maritalStatus: keyof typeof MaritalStatus,
    emailAddress: string,
    homeNumber: string,
    mobileNumber: string,
    nationality: string,
    countryOfResidence: string,
    citizenshipStatus: keyof typeof CitizenshipStatus,
    planToPayLoanRetire: keyof typeof PlanToPayLoanRetire,
    isLendingIntoRetirement: boolean,
  },
  addresses: DatedAddress[],
  contacts: {
    id: string,
    contactDetails: ContactDetails[]
  },
  requiresAdditionalSupport: boolean,
  additionalSupportDetails: {
    id: string
    supportTypes: (keyof typeof SupportTypes)[]
    supportTypeDetails: string
  },
}

export enum PropertyType {
  ConvertedFlatMaisonette = 'Converted Flat / Maisonette',
  DetachedBungalow = 'Detached Bungalow',
  DetachedHouse = 'Detached House',
  EndTerracedBungalow = 'End Terraced Bungalow',
  EndTerracedHouse = 'End Terraced House',
  PurposeBuiltFlatMaisonette = 'Purpose Built Flat / Maisonette',
  SemiDetachedBungalow = 'Semi-Detached Bungalow',
  SemiDetachedHouse = 'Semi-Detached House',
  TerracedBungalow = 'Terraced Bungalow',
  TerracedHouse = 'Terraced House',
}

export enum ConstructionType {
  Standard = 'Standard',
  SystemBuilt = 'System Built (PRC, LPS)',
  TimberFrame = 'Timber frame',
  SteelFrame = 'Steel frame (BISF)',
  FlatRoof = 'Flat roof',
  Granite = 'Granite',
  Whinstone = 'Whinstone',
  Sandstone = 'Sandstone',
  Limestone = 'Limestone',
}

export enum Tenure {
  Leasehold = 'Leasehold',
  FreeholdOrFeudal = 'Freehold/Feudal',
}

export enum PropertyValueType {
  BasedOnAvm = 'Based On Avm',
  BaseOnEstimateOfApplicant = 'Base On Estimate Of Applicant',
}

export type Property = {
  id: string,
  propertyValue: number,
  wasBoughtThroughSharedOwnershipOrHelpToBuy: boolean,
  purchasingTheFullShareWithThisLoanAdvance: boolean,
  estimatedPropertyValue: number,
  propertyValueType: keyof typeof PropertyValueType,
  passesValuationCriteria: boolean,
  isExCouncil: boolean,
  isRtbPurchase: boolean,
  purchasePrice: number,
  amountOfDiscount: number,
  dateOfPurchase: string,
  propertyType: keyof typeof PropertyType,
  bedrooms: number,
  livingRooms: number,
  floorsInBlock: number,
  constructionType: keyof typeof ConstructionType,
  isPropertyAboveCommercialPremises: boolean,
  tenure: keyof typeof Tenure,
  monthlyServiceCharge: number,
  remainingLease: number,
  monthlyGroundRent: number,
  isUpdateWithMonthlyGroundRent: boolean,
  isUpdateWithMonthlyServiceCharge: boolean,
  isParking: boolean,
}

export type AdvisorDetails = {
  id: string,
  wasResponsibleForAdvice?: boolean,
  fcaNumber?: string,
  registeredCompanyName?: string,
  webAddress?: string,
  emailAddress?: string,
  telephoneNumber?: string,
  advisorAddress?: Address,
}

export type Mortgage = {
  id: string,
  outstandingBalance: number,
}

export type Disclosure = {
  id: string,
  anyCriminalConvictions: boolean,
  anyPreviousPropertyRepossessed: boolean,
}

export enum VerificationResultType {
  Pass = 'Pass',
  Fail = 'Fail',
}

export type DirectDebit = {
  id: string,
  bankDetails: {
    id: string,
    accountNumber: string,
    sortCode: string,
    nameOnAccount: {
      id: string,
      applicantId: string,
      applicantName: string,
    }[],
  }
  verificationOutcome?: {
    verificationResultId: string,
    verificationResult: VerificationResultType | null,
  },
  canVerify: boolean,
  preferredDueDate: number,
}

export enum SourceType {
  Equifax = 'Equifax',
  AddressSearch = 'AddressSearch',
}

export enum RecordType {
  Equifax = 'Equifax',
  Declared = 'Declared',
}

export enum AddressType {
  BuyToLet = 'Buy-to-let',
  SecondProperty = 'Second property',
}

export type RentalIncomeAndExpenditures = {
  id: string,
  address: Address,
  isAddedToTable: boolean,
  canBeDeleted: boolean,
  recordType: keyof typeof RecordType,
  source: keyof typeof SourceType,
  addressType: keyof typeof AddressType,
  rentalIncomePerMonth: number,
  expenditurePerMonth: number,
  mortgagePaymentPerMonth: number,
};

export type ChildrenUnder18 = {
  id: string,
  age: number,
};

export enum Relationship {
  Spouse = 'Spouse',
  Partner = 'Partner',
  Child = 'Child',
  Parent = 'Parent',
  Lodger = 'Lodger',
  Tenant = 'Tenant',
  OtherFamilyMember = 'Other family member',
  OneAndTheSame = 'One and the same',
}

export enum Source {
  ElectoralRegister = 'Electoral register',
  Declared = 'Declared',
}

export type OtherOccupant = {
  id: string,
  status: boolean,
  name: string
  relationship: keyof typeof Relationship,
  source: keyof typeof Source,
  currentlyLivingInTheProperty: boolean,
  dependent: boolean,
};

export type Account = {
  updateCount: number,
  dirtyCount: number,
  initiator: string,

  id: string,
  errors: Errors,
  warnings: Errors,

  consentToSearch: string,
  consentToSubmit: {
    htmlText: string,
    moment: string,
  },
  productsResultDigest: string | null,

  stage: Stages,
  status: keyof typeof ApplicationStatuses,
  brokerageName: string,
  applicants: Applicant[],
  securityAddress: Address,
  rentalIncomeAndExpenditures: RentalIncomeAndExpenditures[],
  income: {
    id: string,
    version: number,
    totalHouseholdGrossMonthlyIncome: number,
    totalHouseholdNetMonthlyIncome: number,
  },
  householdCompositions: {
    id: string,
    numberOfDependentsUnder18: number,
    numberOfDependentsAbove18: number,
    listChildrenUnder18: ChildrenUnder18[],
    otherOccupants: OtherOccupant[],
  },
  expenditure: {
    id: string,
    enabledExpenditureFields: string[],
    essentialExpenditure: number,
    councilTax: number,
    nonEssentialExpenditure: number,
    transportExpenditure: number,
    declaredCouncilTaxAboveMinimumTolerance: boolean,
    essentialExpenditureTooltip: string[],
    nonEssentialExpenditureTooltip: string[],
    insurances: number,
    version: number,
  },
  property: Property,
  mortgage: Mortgage,
  loanDetails: LoanDetails,
  disclosure: Disclosure,
  advisorDetails: AdvisorDetails,
  dmpDetails: {
    id: string
    everBeenInDMP: boolean,
    dateLastEnterDMP: string,
    currentlyInDMP: boolean,
    dateLastExitDMP: string,
  },
  directDebit: DirectDebit,
  onsVersionData: {
    nonEssentialExpenditure: number,
    essentialExpenditure: number,
  },
  isAmountMoreThanExpectedLevel: boolean,
  applicationNumber: string | null,
}

export enum TransitionErrorType {
  Validation = 'Validation',
  Rule = 'Rule',
  Checklist = 'Checklist',
  Task = 'Task',
  ThirdParty = 'ThirdParty',
}

export enum ErrorDisplayType {
  Modal = 'Modal',
  Tooltip = 'Tooltip',
  Toast = 'Toast',
}

export type TransitionError = {
  type: TransitionErrorType;
  messages: string[];
  displayType?: ErrorDisplayType,
}
